import { PropsWithChildren } from "react";
import { usePathname } from "next/navigation";
import { useWindowScrolledMoreThan } from "hooks/useWindowScrolledMoreThan";
import { selectBookingStore, selectDaysLength } from "stores/booking/bookingSlice";
import { useAppSelector } from "stores/hooks";
import { diffDays, formatDateRangeWithoutLeadingZero, getFormattedDate } from "utils/dates";
import { formatDateRange } from "utils/helper";
import { useMediaQuery } from "utils/hooks/useMediaQuery";
export const CalendarAndGuestContols = ({
  isOpen,
  onClose,
  onOpen,
  isDisabled,
  children,
  endDate,
  startDate,
  isDateSelected,
  displayAllInPricing
}: PropsWithChildren<{
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  isDisabled?: boolean;
  isDateSelected?: boolean;
  startDate: Date | null;
  endDate: Date | null;
  displayAllInPricing: boolean;
}>) => {
  const bookingStore = useAppSelector(selectBookingStore);
  const [isLargerThan992] = useMediaQuery("(min-width: 992px)");
  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");
  const [isLargerThan456] = useMediaQuery("(min-width: 456px)");
  const [isMobile] = useMediaQuery("(max-width: 767px)");
  const daysLength = useAppSelector(selectDaysLength);
  const scrolled = useWindowScrolledMoreThan(112);
  const pathname = usePathname();
  const isPaymentPage = pathname?.startsWith("/payment");
  const isDetailPage = pathname?.startsWith("/detail");
  const {
    searchStartDate,
    searchEndDate
  } = getFormattedDate(bookingStore?.startDate, bookingStore?.endDate);
  const dateRange = formatDateRange(searchStartDate, searchEndDate); // format date range for display dates in input field
  const noOfNightsText = (checkInDate: string, checkoutDate: string) => {
    const nights = diffDays(new Date(checkInDate), new Date(checkoutDate));
    return `${nights} night${nights > 1 ? "s" : ""}`;
  };
  return <div className={`flex w-full items-center
      ${isDetailPage ? `${displayAllInPricing ? "mb-2.5 h-4 border-b-0 bg-white md:mb-0" : "border-b border-b-[#C0C5D2] bg-[#C6EFEE]"} mb-2.5 !items-start border-b-0 bg-white msm:!items-center md:mb-0 md:border-b-0 md:bg-white` : ``}`} data-sentry-component="CalendarAndGuestContols" data-sentry-source-file="CalendarAndGuestContols.tsx">
      {/* Left section: Date selector */}
      <div className={`${!isDetailPage ? "w-full" : ""} ${isDetailPage && isLargerThan456 ? `p-0` : "md:py-0"} ${isPaymentPage ? "!w-auto md:!w-full" : ""}`}>
        {/* On payment page, show only when not mobile and on other pages, show only when isLargerThan456 */}
        {(isPaymentPage && !isMobile || !isPaymentPage && isLargerThan456) && <p className={`pb-1 text-left leading-[17px] ${isDetailPage || displayAllInPricing ? `hidden` : "text-sm text-textColor md:text-[#606161]"} ${isPaymentPage ? "font-semibold md:font-medium" : "font-medium"}`}>
            {(isDetailPage || isPaymentPage) && !(!isDateSelected || !startDate && !endDate || !bookingStore?.startDate || !bookingStore?.endDate) ? `${daysLength} Night${daysLength > 1 ? "s" : ""}` : "Dates"}
          </p>}
        <button id='MobileCalendarTrigger' onClick={() => !isDisabled && !isLargerThan992 && (isOpen ? onClose() : onOpen())} disabled={isDisabled} className={`${isDetailPage ? `!h-[13px] justify-start text-xs font-medium text-Gray-700 underline xsm:text-[13px] md:h-[15.6px]` : !isPaymentPage ? "text-base font-medium text-[#606161]" : ""} btn w-full overflow-ellipsis whitespace-nowrap rounded-[5px] ${isDetailPage ? " h-7 p-0 md:justify-start" : !isPaymentPage ? " h-12 border border-[#999A9A] px-2 py-2" : ""} ${isPaymentPage ? ` h-4 justify-start p-0 text-[13px] font-medium text-[#717171] md:h-7 md:text-[13px] md:text-textColor ${scrolled ? "md:underline" : "underline"}` : ""}`}>
          {!isDateSelected || !startDate && !endDate || !bookingStore?.startDate || !bookingStore?.endDate ? "Choose Dates" : isDetailPage && !isLargerThan768 && searchStartDate && searchEndDate && dateRange && !displayAllInPricing ? `${dateRange}` : scrolled && !isLargerThan456 ? noOfNightsText(bookingStore.startDate, bookingStore.endDate) : formatDateRangeWithoutLeadingZero(bookingStore.startDate, bookingStore.endDate)}
        </button>
      </div>
      <span className={`block px-1 text-sm font-semibold leading-[14px] text-Gray-700 ${isDetailPage ? "mt-[3px]" : ""}`}>
        ·
      </span>
      {/* Right: GuestModule (children) */}
      {children}
    </div>;
};